import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, watch, computed } from "vue";
  import plural from "@/features/plural";
  import { useStore } from "vuex";
  import { POSITION, useToast } from "vue-toastification";
  import router from "@/router";
  import { debounce } from "@/helpers/debounce";
  import { formatPrice } from "@/helpers/transformation/price";
  import { EstateInterface } from "@/types/EstateInterface";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import { ReportsEstateSelected } from "@/enums/ReportsEstateSelected";
  import { getLotStatusClass } from "@/helpers/status";
  import PackageRow from "@/components/concierge/PackageRow.vue";
  import ConfirmDeleteModalVue from "@/components/ui/modals/ConfirmDeleteModal.vue";
  import { BuilderQuote } from "@/types/BuilderQuote";
  import { copyToClipboard } from "@/helpers/copyToClipboard";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AdminPackages',
  setup(__props, { expose: __expose }) {
  __expose();

  const store = useStore();

  const loading = ref(true);
  const tableHeaders = [
    "Project",
    "Lot",
    "Builder",
    "Design",
    "Land Price",
    "Build Price",
    "Package Price",
    "Status",
    "Action",
  ];
  const packages = ref<BuilderQuote[]>([]);
  const estates = ref<EstateInterface[]>([]);
  const lotDetails = ref<Record<string, { status: string; size: string }>>({});
  const houseDetails = ref<
    Record<
      string,
      {
        area: string;
        bedroom: number;
        bathroomQuantity: number;
        loungeRoomQuantity: number;
      }
    >
  >({});
  const toast = useToast();
  const estateSelected = ref<string>(ReportsEstateSelected.ALL_PROJECTS);
  const selectedEstateId = ref<string | null>(null);
  const isDetailsPackageOpen = ref<string | null>(null);
  const isDisplayDetailsEffect = ref(false);
  const isConfirmDeletePackageModalOpen = ref(false);
  const packageSelectedDeleteId = ref<string | null>(null);

  onMounted(() => {
    getPackages();
    getEstates();
  });

  watch(
    () => estateSelected.value,
    () => {
      debounceSearch();
    }
  );

  const getPackages = async () => {
    try {
      const res = await store.dispatch("builder/getPackages", {
        estateId: selectedEstateId.value,
      });

      packages.value = res.data;
      fetchLotDetails();
      fetchHouseDetails();
    } catch (error) {
      toast.error("Error getting packages");
    } finally {
      loading.value = false;
    }
  };

  const getEstates = async () => {
    try {
      const res = await store.dispatch("estate/getEstates");

      estates.value = res.data;
    } catch (error) {
      toast.error("Error getting estates");
    } finally {
      loading.value = false;
    }
  };

  const fetchLotDetails = async () => {
    for (const savedPackage of packages.value) {
      const lotId = savedPackage.selections.lot.lotId;

      if (lotId && !lotDetails.value[lotId]) {
        try {
          const res = await store.dispatch("lot/getOpenLot", lotId);

          lotDetails.value[lotId] = {
            status: res.data.status,
            size: res.data.size,
          };
        } catch {
          toast.error("Error fetching lot details", {
            position: POSITION.TOP_LEFT,
          });
          lotDetails.value[lotId] = { status: "-", size: "-" };
        }
      }
    }
  };

  const fetchHouseDetails = async () => {
    for (const savedPackage of packages.value) {
      const houseId = savedPackage.selections.house.houseId;

      if (houseId && !houseDetails.value[houseId]) {
        try {
          const res = await store.dispatch(
            "builder/getHouseById",
            houseId.toString()
          );

          houseDetails.value[houseId] = {
            area: res.data.area,
            bedroom: res.data.bedroom,
            bathroomQuantity: res.data.bathroom.quantity,
            loungeRoomQuantity: res.data.loungeRoom.quantity,
          };
        } catch {
          toast.error("Error fetching build size", {
            position: POSITION.TOP_LEFT,
          });
          houseDetails.value[houseId] = {
            area: "-",
            bedroom: 0,
            bathroomQuantity: 0,
            loungeRoomQuantity: 0,
          };
        }
      }
    }
  };

  const optionsEstate = computed(() => [
    { title: ReportsEstateSelected.ALL_PROJECTS, id: null },
    ...estates.value.map((estate) => ({ title: estate.title, id: estate._id })),
  ]);

  const onSelectEstate = (estateTitle: string, estateId: string | null) => {
    estateSelected.value = estateTitle;
    selectedEstateId.value = estateId;
  };

  const getEstateTitle = (estateId: string) => {
    const estate = estates.value.find((estate) => estate._id === estateId);

    return estate ? estate.title : "-";
  };

  const getLotStatus = (lotId: string) => {
    return lotDetails.value[lotId]?.status || "-";
  };

  const getLotClass = (lotId: string) => {
    const status = lotDetails.value[lotId]?.status || "-";
    const statusClass = getLotStatusClass(status);

    return statusClass;
  };

  const getLandSize = (lotId: string) => {
    return formatPrice(lotDetails.value[lotId]?.size || "-", false);
  };

  const getBuildSize = (houseId: string) => {
    return formatPrice(houseDetails.value[houseId]?.area || "-", false);
  };

  const getBedroomCount = (houseId: string) => {
    return houseDetails.value[houseId]?.bedroom || "-";
  };

  const getBathroomCount = (houseId: string) => {
    return houseDetails.value[houseId]?.bathroomQuantity || "-";
  };

  const getLoungeRoomCount = (houseId: string) => {
    return houseDetails.value[houseId]?.loungeRoomQuantity || "-";
  };

  const debounceSearch = debounce(getPackages);
  const onClickPackage = (id: string) => {
    router.push({ name: "AdminPackageUpdate", params: { packageId: id } });
  };
  const toCreatePackage = () => {
    router.push({ name: "AdminPackageCreate" });
  };
  const onDeletePackage = async (id: string) => {
    packageSelectedDeleteId.value = id;
    isConfirmDeletePackageModalOpen.value = true;
  };

  const onConfirmDeletePackage = async () => {
    loading.value = true;

    try {
      await store.dispatch(
        "builder/deletePackage",
        packageSelectedDeleteId.value
      );
      toast.success("Package deleted");
      router.push({ name: "AdminPackages" });
      getPackages();
    } catch (error) {
      toast.error("Deleting package failed");
    } finally {
      loading.value = false;
    }
  };

  const onCopyLinkPackage = async (savedPackage: BuilderQuote) => {
    const link = `${window.location.origin}/builder/estate/${savedPackage.estateId}/borrow-calculator/request-quote/${savedPackage._id}/house/${savedPackage.selections.house.houseId}/loan-amount/600000`;

    copyToClipboard(link, "Saved Package link was copied to clipboard");
  };

  const openDetailsPackage = (id: string) => {
    isDetailsPackageOpen.value = isDetailsPackageOpen.value === id ? null : id;

    setTimeout(() => {
      isDisplayDetailsEffect.value = isDetailsPackageOpen.value === id;
    }, 50);
  };

const __returned__ = { store, loading, tableHeaders, packages, estates, lotDetails, houseDetails, toast, estateSelected, selectedEstateId, isDetailsPackageOpen, isDisplayDetailsEffect, isConfirmDeletePackageModalOpen, packageSelectedDeleteId, getPackages, getEstates, fetchLotDetails, fetchHouseDetails, optionsEstate, onSelectEstate, getEstateTitle, getLotStatus, getLotClass, getLandSize, getBuildSize, getBedroomCount, getBathroomCount, getLoungeRoomCount, debounceSearch, onClickPackage, toCreatePackage, onDeletePackage, onConfirmDeletePackage, onCopyLinkPackage, openDetailsPackage, ref, onMounted, watch, computed, get plural() { return plural }, get useStore() { return useStore }, get POSITION() { return POSITION }, get useToast() { return useToast }, get router() { return router }, get debounce() { return debounce }, get formatPrice() { return formatPrice }, get EstateInterface() { return EstateInterface }, DropDown, get ReportsEstateSelected() { return ReportsEstateSelected }, get getLotStatusClass() { return getLotStatusClass }, PackageRow, ConfirmDeleteModalVue, get BuilderQuote() { return BuilderQuote }, get copyToClipboard() { return copyToClipboard } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})